import React, { useEffect, useRef, useState } from "react";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import { CircularProgress, Skeleton } from "@mui/material";
import ReactPaginate from "react-paginate";
import DateConverter from "@heartinz/date_and_time_processor";
import { useAuth } from "../../../context/AuthProvider";
import AxiosInstance from "../../../utils/AxiosInstance";
import { useNetwork } from "../../../context/NetworkContext";
import handleError from "../../../constants/handleError";
import { usePermission } from "../../../context/PermissionContext";
import Messages from "../../../constants/Messages";
import { useLocation, useNavigate } from "react-router-dom";
import GetServerLiveTime from "../../../constants/GetServerLiveTime";
import dayjs from "dayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import { useShowAlert } from "../../../context/AlertContext";
import EssentialInvoice from "../../../utils/print/a4Invoices/EssentialInvoice";
import ProfessionalGSTInvoice from "../../../utils/print/a4Invoices/ProfessionalGSTInvoice";
import { ChevronRightIcon, EditIcon, FilterAltIcon, ShowIcon } from "../../../constants/Icons";

const ProductSale = ({ productId }) => {
  const { isUser } = useAuth();
  const { checkNetwork } = useNetwork();
  const navigate = useNavigate();
  const showAlert = useShowAlert();
  const hasFetched = useRef(false);
  const [isSaleData, setSaleData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const serverTime = GetServerLiveTime();
  const [isViewModalOpen, setViewModalOpen] = useState(false);
  const [isViewData, setViewData] = useState(null);
  const [buttonLoading, setButtonLoading] = useState({ type: "", id: null });
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isEditData, setEditData] = useState(null);
  const [showCustom, SetShowCustom] = useState(false);
  const { storeSettings } = usePermission();
  const [selectedFilter, setSelectedFilter] = useState("today");
  const templateId =
    storeSettings?.invoiceSettings?.template?.templateId || "INVOICEONE";
  const previousDate = new Date(serverTime.getTime() - 1000);
  const [fromDate, setFromDate] = useState(previousDate);
  const [toDate, setToDate] = useState(serverTime);

  const formatTrackMode = (salemode) => {
    switch (salemode?.toUpperCase()) {
      case "BILL":
        return "BILL RECEIPT";
      case "INVOICE":
        return "TAX INVOICE";
      default:
        return salemode;
    }
  };

  const getBgColor = (salemode) => {
    if (!salemode || typeof salemode !== "string") return "bg-label-secondary";
    switch (salemode.toLowerCase()) {
      case "bill":
        return "bg-label-honey";
      case "invoice":
        return "bg-label-moon";
      default:
        return "bg-label-secondary";
    }
  };

  const getBgTxtColor = (salemode) => {
    if (!salemode || typeof salemode !== "string") return "bg-label-secondary";
    switch (salemode.toLowerCase()) {
      case "bill":
        return "bg-label-wave";
      case "invoice":
        return "bg-label-deep-purple";
      default:
        return "bg-label-secondary";
    }
  };

  const getColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "bg-label-success";
      case "cancelled":
        return "bg-label-danger";
      default:
        return "bg-label-secondary";
    }
  };

  const calculateDate = (baseDate, offsetDays) => {
    const resultDate = new Date(baseDate);
    resultDate.setDate(resultDate.getDate() + offsetDays);
    return resultDate;
  };

  const handleFromDateChange = (newValue) => {
    if (!newValue) return;

    const selectedDate = newValue.toDate();
    setFromDate(selectedDate);

    const toDateValue = new Date(toDate);
    if (selectedDate > toDateValue) {
      const newFromDate = new Date(toDateValue.getTime() - 1000);
      setFromDate(newFromDate);
      return;
    }

    const minToDate = calculateDate(selectedDate, -64);
    const maxToDate = new Date(
      Math.min(calculateDate(selectedDate, 64), serverTime),
    );

    if (toDateValue < minToDate || toDateValue > maxToDate) {
      setToDate(selectedDate);
    }
  };

  const handleToDateChange = (newValue) => {
    if (!newValue) return;

    const selectedToDate = newValue.toDate();
    const fromDateValue = new Date(fromDate);

    if (selectedToDate.getTime() <= fromDateValue.getTime()) {
      const correctedFromDate = new Date(selectedToDate.getTime() - 1000);
      setFromDate(correctedFromDate);
    }

    setToDate(selectedToDate);
  };

  const toDateMin = calculateDate(new Date(fromDate), -64);
  const toDateMax = new Date(
    Math.min(calculateDate(new Date(fromDate), 64), serverTime),
  );

  const handleQuickFilter = (filter, page = 0) => {
    setSelectedFilter(filter);
    if (filter === "custom") {
      // Customize opt
      SetShowCustom(true);
      return;
    }

    // Filtered ranges
    let startDate, endDate;

    if (filter === "today") {
      const now = new Date();
      startDate = new Date(now);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(now);
      endDate.setHours(0, 0, 0, 999);
    } else if (filter === "yesterday") {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);

      startDate = new Date(yesterday);
      startDate.setHours(0, 0, 0, 0);

      endDate = new Date(yesterday);
      endDate.setHours(23, 59, 59, 999);
    } else {
      ({ startDate, endDate } = DateConverter?.getDateRange(filter));
      startDate = new Date(startDate);
      endDate = new Date(endDate);
      endDate.setHours(0, 0, 0, 999);
    }

    setFromDate(startDate);
    setToDate(endDate);
    setFromDate(startDate);
    setToDate(endDate);

    SetShowCustom(false);
  };

  const handleListSale = async (
    productId,
    fromDate = fromDate,
    toDate = toDate,
  ) => {
    setIsLoading(true);
    try {
      if (!checkNetwork()) return;
      var data = {
        fromdate: encodeURIComponent(
          DateConverter.convertToTimeZone(
            fromDate.toISOString(),
            isUser.timezone,
          ),
        ),
        todate: encodeURIComponent(
          DateConverter.convertToTimeZone(
            toDate.toISOString(),
            isUser.timezone,
          ),
        ),
      };
      const response = await AxiosInstance.get(
        `module/reports/${productId}?fromdate=${data.fromdate}&todate=${data.todate}`,
      );
      const result = response.data.results || [];
      setSaleData(result);
      setIsLoading(false);
    } catch (err) {
      handleError(err, showAlert);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaleItems = async (saleId) => {
    try {
      if (!checkNetwork()) return;
      const response = await AxiosInstance.get(`module/sale/${saleId}`);
      return response.data.results;
    } catch (error) {
      handleError(error, showAlert);
      return false;
    }
  };

  const handleView = async (item) => {
    setButtonLoading({ type: "view", id: item._id });
    try {
      const data = await handleSaleItems(item._id);
      if (data) {
        setViewData(data);
        setViewModalOpen(true);
      }
    } catch (error) {
      console.error("Error fetching view data:", error);
    } finally {
      setButtonLoading({ type: "", id: null });
    }
  };

  const handleEdit = async (item) => {
    setButtonLoading({ type: "edit", id: item._id });
    try {
      const data = await handleSaleItems(item._id);

      if (data) {
        if (data.consumeVersion === "CONSUMEV2") {
          navigate("/editsale", { state: data });
        } else {
          navigate("/editsaleold", { state: data });
        }
      }
    } catch (error) {
      console.error("Error loading sale data:", error);
    } finally {
      setButtonLoading({ type: "", id: null });
    }
  };

  const handleClose = () => {
    setViewModalOpen(false);
    setViewData(null);
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    if (productId) {
      handleListSale(productId, fromDate, toDate);
    }
  }, [productId]);
  return (
    <div>
      <div className="col-lg-12">
        <div className="card">
          <div className="card-body">
            <div className="row g-3 align-items-end">
              {showCustom && (
                <>
                  <div className="col-2">
                    <label className="form-label w-100">
                      From Date <span className="text-danger"> *</span>
                    </label>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        value={fromDate ? dayjs(fromDate) : null}
                        maxDate={dayjs(serverTime)}
                        format="DD MMM YYYY"
                        onChange={handleFromDateChange}
                        slotProps={{
                          textField: {
                            className: "form-control",
                            size: "small",
                            inputProps: { readOnly: true },
                            onKeyDown: (e) => e.preventDefault(),
                            sx: {
                              "& .MuiInputBase-input": {
                                cursor: "pointer",
                              },
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </div>
                  <div className="col-2">
                    <label className="form-label w-100">
                      To Date <span className="text-danger"> *</span>
                    </label>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        value={toDate ? dayjs(toDate) : null}
                        minDate={toDateMin ? dayjs(toDateMin) : undefined}
                        maxDate={toDateMax ? dayjs(toDateMax) : undefined}
                        format="DD MMM YYYY"
                        onChange={handleToDateChange}
                        slotProps={{
                          textField: {
                            className: "form-control",
                            size: "small",
                            inputProps: { readOnly: true },
                            onKeyDown: (e) => e.preventDefault(),
                            sx: {
                              "& .MuiInputBase-input": {
                                cursor: "pointer",
                              },
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </div>
                </>
              )}
              <div className="col-lg-2 mb-2 mb-lg-0 ">
                <div className="input-group">
                  <span className="input-group-text">
                    <FilterAltIcon/>
                  </span>
                  <select
                    className="form-select"
                    onChange={(e) => handleQuickFilter(e.target.value)}
                    value={selectedFilter} // today will be selected by default
                  >
                    <option value="today">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="thisWeek">This Week</option>
                    <option value="lastWeek">Last Week</option>
                    <option value="thisMonth">This Month</option>
                    <option value="lastMonth">Last Month</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
              </div>
              <div className="col-2 float-end">
                <div className="d-grid">
                  {isLoading ? (
                    <button
                      className="btn btn-primary pb-0 text-right"
                      disabled
                    >
                      <CircularProgress size={22} className="text-white" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-primary"
                      id="salesreportbtn"
                      onClick={() =>
                        handleListSale(productId, fromDate, toDate)
                      }
                    >
                      Generate <ChevronRightIcon/>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="table-responsive mt-3">
        <table className="table align-middle mb-0">
          <thead className="table-light text-center">
            <tr>
              <th>S.No</th>
              <th>Sale Mode</th>
              <th>Sale No</th>
              <th>Date</th>
              <th>Sale Items</th>
              <th>Grand Total</th>
              {/* <th>Status</th> */}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              [...Array(10)].map((_, index) => (
                <tr key={index}>
                  {[...Array(7)].map((__, i) => (
                    <td key={i} className="text-center">
                      <Skeleton variant="text" width={i === 0 ? 50 : 100} />
                    </td>
                  ))}
                </tr>
              ))
            ) : isSaleData?.reportData?.length > 0 ? (
              isSaleData?.reportData?.map((sale, index) => (
                <tr
                  key={sale._id || index}
                  className="cursor-poInter hover-row"
                  onClick={() => handleView(sale)}
                >
                  <td className="text-center">{index + 1}</td>
                  <td className="text-center">
                    {sale?.salemode && (
                      <span
                        className={`badge rounded-pill ${getBgColor(
                          sale?.salemode,
                        )}`}
                      >
                        {formatTrackMode(sale?.salemode)}
                      </span>
                    )}
                  </td>
                  <td className="text-center">
                    {sale?.salemode && (
                      <span
                        className={`badge rounded-pill ${getBgTxtColor(
                          sale?.salemode,
                        )}`}
                      >
                        {sale?.saleno}
                      </span>
                    )}
                  </td>
                  <td className="text-center">
                    <div className="d-flex flex-column">
                      <span className="text-nowrap text-heading fw-medium">
                        {
                          DateConverter?.ISOToDisplayFormat(
                            DateConverter?.convertToTimeZone(
                              sale?.saledate,
                              isUser.timezone,
                            ),
                          )?.date
                        }
                      </span>
                    </div>
                  </td>
                  <td className="text-center">{sale?.quantity || 0} Item(s)</td>
                  <td className="text-center">
                    ₹
                    {CurrencyConverter.toCurrency(
                      Number(sale?.lineGrandTotal) || 0,
                      "INR",
                    )}
                  </td>
                  {/* <td className="text-center">
                  {sale?.status && (
                    <span
                      className={`badge rounded-pill ${getColor(sale?.status)}`}
                    >
                      {sale?.status.charAt(0).toUpperCase() +
                        sale?.status.slice(1).toLowerCase()}
                    </span>
                  )}
                </td> */}
                  <td className="text-center">
                    <div className="order-actions opacity-0 opacity-md-100">
                      <button
                        type="button"
                        className="btn-label-info shadow-none me-2"
                        onClick={() => handleView(sale)}
                        disabled={
                          buttonLoading?.id === sale?._id &&
                          buttonLoading?.type === "view"
                        }
                      >
                        {buttonLoading?.id === sale?._id &&
                        buttonLoading?.type === "view" ? (
                          <CircularProgress
                            size={20}
                            className="text-primary"
                          />
                        ) : (
                          <ShowIcon/>
                        )}
                      </button>
                      <button
                        type="button"
                        className="btn-label-primary shadow-none me-2"
                        onClick={() => handleEdit(sale)}
                        disabled={
                          buttonLoading?.id === sale?._id &&
                          buttonLoading?.type === "edit"
                        }
                      >
                        {buttonLoading?.id === sale?._id &&
                        buttonLoading?.type === "edit" ? (
                          <CircularProgress
                            size={20}
                            className="text-primary"
                          />
                        ) : (
                          <EditIcon/>
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="text-center p-2 text-muted">
                  No Stock found in the store. add some Stock to get started.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {templateId === "INVOICEONE" && isViewModalOpen && (
        <ProfessionalGSTInvoice
          isData={isViewData}
          selectedOption={templateId}
          onClose={handleClose}
          onDataUpdate={async () => {
            const data = await handleSaleItems(isViewData?._id);
            if (data) setViewData(data);
          }}
          onDataChange={true}
        />
      )}
      {templateId === "INVOICETWO" && isViewModalOpen && (
        <EssentialInvoice
          isData={isViewData}
          selectedOption={templateId}
          onClose={handleClose}
          onDataUpdate={async () => {
            const data = await handleSaleItems(isViewData?._id);
            if (data) setViewData(data);
          }}
          onDataChange={true}
        />
      )}
    </div>
  );
};

export default ProductSale;
